import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, RefreshCw, Trash2, Search, LogOut, Loader2, ListOrdered, Plus, X, Send, Check, Pencil, Upload } from "lucide-react";
import { adminLogin, adminListWaitlist, adminDeleteEntry, adminRenumber, adminCreateEntry, adminSendInvite, adminUpdateEntry, adminImportCsv, API } from "@/lib/api";
import { toast } from "sonner";
import AdminAnalytics from "@/components/site/AdminAnalytics";

const STORAGE_KEY = "w_admin_passcode";

export default function Admin() {
  const [passcode, setPasscode] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loginInput, setLoginInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ handle: "", email: "", reason: "" });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState("");
  const [invitingId, setInvitingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const res = await adminImportCsv(await file.text(), passcode);
      toast.success(`Imported ${res.imported} row${res.imported === 1 ? "" : "s"}, skipped ${res.skipped}.`);
      load(passcode);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't import CSV.");
    } finally {
      setImporting(false);
    }
  };

  const load = useCallback(async (pc) => {
    setLoading(true);
    setError("");
    try {
      const data = await adminListWaitlist(pc);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, pc);
      setPasscode(pc);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError("Invalid passcode.");
        setAuthed(false);
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        setError(err?.response?.data?.detail || "Couldn't load list.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (passcode) load(passcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!loginInput.trim()) return;
    setLoading(true);
    setError("");
    try {
      await adminLogin(loginInput.trim());
      await load(loginInput.trim());
      setLoginInput("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid passcode.");
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPasscode("");
    setAuthed(false);
    setEntries([]);
    setTotal(0);
  };

  const onDelete = async (entry) => {
    if (!window.confirm(`Delete @${entry.handle} (${entry.email})? This cannot be undone.`)) return;
    try {
      await adminDeleteEntry(entry.id, passcode);
      toast.success(`@${entry.handle} removed.`);
      await load(passcode);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
    }
  };

  const onEditEmail = async (entry) => {
    const next = window.prompt(
      `Update email for @${entry.handle}\nCurrent: ${entry.email}\n\nEnter new email:`,
      entry.email
    );
    if (next == null) return; // cancelled
    const trimmed = next.trim().toLowerCase();
    if (!trimmed || trimmed === entry.email.toLowerCase()) return;
    try {
      await adminUpdateEntry(entry.id, { email: trimmed }, passcode);
      toast.success(`Email updated for @${entry.handle}.`);
      await load(passcode);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update email.");
    }
  };

  const onInvite = async (entry) => {
    const isResend = !!entry.invited;
    const verb = isResend ? "Resend invite to" : "Send invite to";
    if (!window.confirm(`${verb} ${entry.email} for @${entry.handle}?`)) return;
    setInvitingId(entry.id);
    try {
      await adminSendInvite(entry.id, passcode);
      toast.success(
        isResend
          ? `Invite resent to ${entry.email}.`
          : `Invite sent to ${entry.email}.`
      );
      await load(passcode);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send invite.");
    } finally {
      setInvitingId(null);
    }
  };

  const onExportCsv = async (path = "waitlist.csv", filename = "w-waitlist.csv") => {
    try {
      const res = await fetch(`${API}/admin/${path}`, {
        headers: { "X-Admin-Passcode": passcode },
      });
      if (!res.ok) {
        toast.error("Couldn't export CSV.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't export CSV.");
    }
  };

  const onRenumber = async () => {
    if (!window.confirm("Re-number all entries by signup order? Positions will become 1, 2, 3 …")) return;
    try {
      const data = await adminRenumber(passcode);
      toast.success(
        data.renumbered === 0
          ? "Already in order — nothing to change."
          : `Renumbered ${data.renumbered} of ${data.total} entries.`
      );
      await load(passcode);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't renumber.");
    }
  };

  const onManualAdd = async (e) => {
    e.preventDefault();
    setAddError("");
    const handle = addForm.handle.trim().replace(/^@/, "").toLowerCase();
    const email = addForm.email.trim().toLowerCase();
    const reason = addForm.reason.trim();
    if (!handle || !email) {
      setAddError("Handle and email are required.");
      return;
    }
    setAddSubmitting(true);
    try {
      await adminCreateEntry({ handle, email, reason: reason || undefined }, passcode);
      toast.success(`@${handle} added.`);
      setShowAddModal(false);
      setAddForm({ handle: "", email: "", reason: "" });
      await load(passcode);
    } catch (err) {
      setAddError(err?.response?.data?.detail || "Couldn't add entry.");
    } finally {
      setAddSubmitting(false);
    }
  };

  const filtered = entries.filter((e) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      e.handle?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      (e.reason || "").toLowerCase().includes(q)
    );
  });

  // ---------- Login screen ----------
  if (!authed) {
    return (
      <main data-testid="admin-page" className="relative min-h-screen bg-obsidian text-pearl flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="flex items-center gap-2 mb-8" data-testid="admin-back-row">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 label-overline hover:text-pearl transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to W
            </Link>
            <span className="label-overline">Admin</span>
          </div>
          <h1 className="mt-4 font-serif-display text-pearl text-4xl tracking-tight leading-[1.05]">
            Enter <span className="italic text-bone">passcode</span>.
          </h1>
          <form onSubmit={onLogin} className="mt-10 glass-card rounded-2xl p-6" data-testid="admin-login-form">
            <label className="label-overline block mb-2">Passcode</label>
            <input
              data-testid="admin-passcode-input"
              type="password"
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              autoFocus
              placeholder="••••••••••"
              className="w-full bg-[var(--w-input-bg)] border border-hairline rounded-xl px-4 py-3 text-pearl placeholder:text-ash/50 outline-none focus:border-[#5B9EC9]/40 transition-colors font-mono-label text-[14px]"
            />
            {error && (
              <p data-testid="admin-error" className="mt-3 text-[13px] text-red-300/90">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1E3A5F] text-white py-3 text-[14px] font-medium hover:bg-[#2A4A75] transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  // ---------- Authenticated dashboard ----------
  return (
    <main data-testid="admin-dashboard" className="relative min-h-screen bg-obsidian text-pearl py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* header */}
        <div className="flex flex-col gap-6 mb-10">
          <div>
            <span className="label-overline">Admin · waitlist</span>
            <h1 className="mt-3 font-serif-display text-pearl text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              <span className="italic text-bone">{total.toLocaleString()}</span>{" "}
              {total === 1 ? "reservation" : "reservations"}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              data-testid="admin-add-manual-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-hairline px-4 py-2 text-[12px] text-ash hover:text-pearl hover:border-bone/40 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add manually
            </button>
            <button
              onClick={() => load(passcode)}
              data-testid="admin-refresh-btn"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-hairline px-4 py-2 text-[12px] text-ash hover:text-pearl hover:border-bone/40 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={onRenumber}
              data-testid="admin-renumber-btn"
              disabled={loading || total === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-hairline px-4 py-2 text-[12px] text-ash hover:text-pearl hover:border-bone/40 transition-colors disabled:opacity-60"
            >
              <ListOrdered className="w-3.5 h-3.5" />
              Renumber
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onImportFile}
              className="hidden"
              data-testid="admin-import-csv-input"
            />
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              data-testid="admin-import-csv-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-hairline px-4 py-2 text-[12px] text-ash hover:text-pearl hover:border-bone/40 transition-colors disabled:opacity-60"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Import Waitlist CSV
            </button>
            <button
              onClick={() => onExportCsv("legacy-waitlist.csv", "w-legacy-waitlist.csv")}
              data-testid="admin-export-legacy-csv-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-hairline px-4 py-2 text-[12px] text-ash hover:text-pearl hover:border-bone/40 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export Legacy Waitlist
            </button>
            <button
              onClick={() => onExportCsv()}
              data-testid="admin-export-csv-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-[#1E3A5F] text-white px-4 py-2 text-[12px] font-medium hover:bg-[#2A4A75] transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-hairline px-3 py-2 text-[12px] text-ash hover:text-pearl hover:border-bone/40 transition-colors"
              aria-label="Log out"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* search */}
        <AdminAnalytics passcode={passcode} refreshKey={total} />

        <div className="flex items-center gap-3 mb-6 rounded-lg border border-hairline bg-[var(--w-input-bg)] px-4 py-2 max-w-md">
          <Search className="w-3.5 h-3.5 text-ash" />
          <input
            data-testid="admin-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search handle, email, or note…"
            className="flex-1 bg-transparent outline-none text-pearl placeholder:text-ash/50 text-[14px] py-1.5"
          />
        </div>

        {/* table */}
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1080px]">
            <thead>
              <tr className="border-b border-hairline">
                <th className="px-5 py-3 label-overline font-normal">#</th>
                <th className="px-5 py-3 label-overline font-normal">Handle</th>
                <th className="px-5 py-3 label-overline font-normal">Email</th>
                <th className="px-5 py-3 label-overline font-normal">Reason</th>
                <th className="px-5 py-3 label-overline font-normal">Country</th>
                <th className="px-5 py-3 label-overline font-normal">Created</th>
                <th className="px-5 py-3 label-overline font-normal">Status</th>
                <th className="px-5 py-3 label-overline font-normal text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center text-ash text-[14px]">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center text-ash text-[14px]">
                    {total === 0
                      ? "No reservations yet. The first one will land here."
                      : "Nothing matches your search."}
                  </td>
                </tr>
              )}
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`admin-row-${e.handle}`}
                  className="border-b border-[var(--w-border)] hover:bg-[#4A8DB8]/[0.02] transition-colors"
                >
                  <td className="px-5 py-4 font-mono-label text-[12px] text-ash">
                    {e.position}
                  </td>
                  <td className="px-5 py-4 text-pearl text-[14px] font-mono-label">
                    @{e.handle}
                  </td>
                  <td className="px-5 py-4 text-pearl text-[13px]">{e.email}</td>
                  <td className="px-5 py-4 text-ash text-[13px] max-w-sm truncate">
                    {e.reason || <span className="text-ash/50">—</span>}
                  </td>
                  <td className="px-5 py-4 text-ash text-[13px] whitespace-nowrap">
                    {e.country ? (
                      <span title={e.country_code || ""}>
                        <span className="font-mono-label text-[11px] text-ash/70 mr-1">
                          {e.country_code || ""}
                        </span>
                        {e.country}
                      </span>
                    ) : (
                      <span className="text-ash/40">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-mono-label text-[11px] text-ash whitespace-nowrap">
                    {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    {e.invited ? (
                      <span
                        data-testid={`admin-invited-pill-${e.handle}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-1 font-mono-label text-[10px] tracking-[0.14em] uppercase text-green-400"
                        title={e.invited_at ? `Invited ${new Date(e.invited_at).toLocaleString()}${e.invite_count > 1 ? ` · ${e.invite_count}×` : ""}` : "Invited"}
                      >
                        <Check className="w-3 h-3" strokeWidth={3} />
                        Invited{e.invite_count > 1 ? ` · ${e.invite_count}×` : ""}
                      </span>
                    ) : (
                      <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-ash/40">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => onInvite(e)}
                        data-testid={`admin-invite-${e.handle}`}
                        aria-label={e.invited ? `Resend invite to ${e.email}` : `Send invite to ${e.email}`}
                        title={e.invited ? "Resend invite" : "Send invite"}
                        disabled={invitingId === e.id}
                        className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-2.5 py-1.5 text-[11px] font-mono-label tracking-[0.14em] uppercase text-ash hover:text-pearl hover:border-bone/40 transition-colors disabled:opacity-50"
                      >
                        {invitingId === e.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        {e.invited ? "Resend" : "Invite"}
                      </button>
                      <button
                        onClick={() => onEditEmail(e)}
                        data-testid={`admin-edit-${e.handle}`}
                        aria-label={`Edit email for @${e.handle}`}
                        title="Edit email"
                        className="text-ash hover:text-pearl transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(e)}
                        data-testid={`admin-delete-${e.handle}`}
                        aria-label={`Delete @${e.handle}`}
                        className="text-ash hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        <p className="mt-6 label-overline">
          Showing {filtered.length.toLocaleString()} of {total.toLocaleString()}
        </p>
      </div>

      {/* Manual add modal */}
      {showAddModal && (
        <div
          data-testid="admin-add-modal"
          className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/70"
          onClick={() => !addSubmitting && setShowAddModal(false)}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            className="w-full max-w-md bg-gunmetal border border-hairline rounded-2xl p-7 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="label-overline">Admin</span>
                <h2 className="font-serif-display text-pearl text-2xl mt-1">
                  Add <span className="italic text-bone">manually</span>
                </h2>
              </div>
              <button
                onClick={() => !addSubmitting && setShowAddModal(false)}
                data-testid="admin-add-modal-close"
                aria-label="Close"
                className="text-ash hover:text-pearl transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={onManualAdd} className="space-y-4">
              <div>
                <label className="label-overline block mb-2">Handle</label>
                <div className="flex items-center rounded-lg border border-hairline bg-[var(--w-input-bg)] px-3">
                  <span className="text-ash/60 text-[13px] pr-1">@</span>
                  <input
                    data-testid="admin-add-handle-input"
                    value={addForm.handle}
                    onChange={(e) => setAddForm({ ...addForm, handle: e.target.value })}
                    placeholder="jane.doe"
                    autoFocus
                    className="flex-1 bg-transparent outline-none text-pearl placeholder:text-ash/50 text-[14px] py-3 font-mono-label"
                  />
                  <span className="text-ash/60 text-[12px] pl-2">.xyz</span>
                </div>
                <p className="mt-1.5 text-[11px] text-ash/60">
                  Admin can bypass reserved/blocked list. Format still required.
                </p>
              </div>
              <div>
                <label className="label-overline block mb-2">Email</label>
                <input
                  data-testid="admin-add-email-input"
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  placeholder="jane@somewhere.else"
                  className="w-full bg-[var(--w-input-bg)] border border-hairline rounded-xl px-4 py-3 text-pearl placeholder:text-ash/50 outline-none text-[14px]"
                />
              </div>
              <div>
                <label className="label-overline block mb-2">Note (optional)</label>
                <textarea
                  data-testid="admin-add-reason-input"
                  value={addForm.reason}
                  onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })}
                  rows={2}
                  placeholder="Grandfathered from launch beta…"
                  className="w-full bg-[var(--w-input-bg)] border border-hairline rounded-xl px-4 py-3 text-pearl placeholder:text-ash/50 outline-none text-[13px] resize-none"
                />
              </div>
              {addError && (
                <p data-testid="admin-add-error" className="text-[13px] text-red-300/90">
                  {addError}
                </p>
              )}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !addSubmitting && setShowAddModal(false)}
                  className="flex-1 rounded-lg border border-hairline text-ash py-2.5 text-[13px] hover:text-pearl hover:border-bone/40 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="admin-add-submit-btn"
                  disabled={addSubmitting}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#1E3A5F] text-white py-2.5 text-[13px] font-medium hover:bg-[#2A4A75] transition-colors disabled:opacity-50"
                >
                  {addSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add to waitlist"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
